import { DataBlock, DownloadProgress, DownloadResult } from './types';

const DEBUG = false;
const logger = (...content) => DEBUG && console.log(content);

/**
 * Manages worker-based operations related to data processing and communication.
 * The class provides methods for loading data blocks, downloading and decoding data packs,
 * and cleaning up worker threads after use.
 */
export class WorkerManager {
  private worker: Worker | null = null;

  constructor() {
    this.worker = new Worker(new URL('./worker.ts', import.meta.url), {
      type: 'module'
    });
  }

  async isDataAvailable(sessionId: string): Promise<{isDataAvailable: boolean}> {
    if (!this.worker) {
      throw new Error('isDataAvailable - Worker non disponibile');
    }

    return new Promise((resolve, reject) => {
      const messageHandler = (event: MessageEvent) => {
        const { type, payload } = event.data;

        switch (type) {
          case 'DATA_AVAILABLE':
            this.worker?.removeEventListener('message', messageHandler);
            return resolve(payload);
            break;

          case 'ERROR':
            this.worker?.removeEventListener('message', messageHandler);
            return reject(new Error(payload.message));
            break;
        }
      }

      this.worker?.addEventListener('message', messageHandler);

      // Invia messaggio al worker
      this.worker?.postMessage({
        type: 'CHECK_DATA_IS_AVAILABLE',
        payload: {
          sessionId,
        }
      });
    })
  }

  /**
   * Loads a data block associated with a specific session ID and timestamp by communicating with a worker.
   *
   * @param sessionId - The unique identifier of the session for which the data should be loaded.
   * @param timestamp - The timestamp corresponding to the data block to retrieve.
   * @return A Promise that resolves with the loaded data block if successful or rejects with an error if the operation fails.
   * @throws Error if the worker is not available or if the worker process returns an error.
   */
  async loadDataBlock(
    sessionId: string,
    timestamp: number | null,
  ): Promise<DataBlock> {
    if (!this.worker) {
      throw new Error('Worker non disponibile');
    }

    logger('loadDataBlock - worker ok')

    return new Promise((resolve, reject) => {
      logger('promise')
      const messageHandler = (event: MessageEvent) => {
        logger('message handler')
        const { type, payload } = event.data;
        switch (type) {
          case 'SUCCESS':
            logger('success')
            this.worker?.removeEventListener('message', messageHandler);
            return resolve(payload);
            break;

          case 'ERROR':
            logger('error')
            this.worker?.removeEventListener('message', messageHandler);
            return reject(new Error(payload.message));
            break;
        }
      }

      logger('add event listener')
      this.worker?.addEventListener('message', messageHandler);

      logger('postMessage')
      // Invia messaggio al worker
      this.worker?.postMessage({
        type: 'GET_DATA_BY_SESSION_TIMESTAMP',
        payload: {
          sessionId,
          timestamp,
        }
      });

      logger('end')
    })
  }

  /**
   * Downloads data from a specified URL and decodes it using a worker thread.
   *
   * @param sessionId The ID of the current session.
   * @param athleteId The ID of the athlete for whom data is being retrieved.
   * @param baseUrl The base URL from which the data is to be downloaded.
   * @param additionalParams Optional additional parameters to include in the request.
   * @param onProgress Optional callback function to report download progress.
   * @return A promise that resolves with the downloaded and decoded data or rejects with an error.
   */
  async downloadFromUrlAndDecodePack(
    sessionId: string,
    athleteId: string,
    baseUrl: string,
    additionalParams?: Record<string, string>,
    onProgress?: (progress: DownloadProgress) => void
  ): Promise<DownloadResult> {
    if (!this.worker) {
      throw new Error('Worker non disponibile');
    }

    return new Promise((resolve, reject) => {
      const messageHandler = (event: MessageEvent) => {
        const { type, payload } = event.data;

        switch (type) {
          case 'PROGRESS':
            onProgress?.(payload);
            break;

          case 'SUCCESS':
            this.worker?.removeEventListener('message', messageHandler);
            resolve(payload);
            break;

          case 'ERROR':
            this.worker?.removeEventListener('message', messageHandler);
            reject(new Error(payload.message));
            break;
        }
      };

      this.worker?.addEventListener('message', messageHandler);

      // Invia messaggio al worker
      this.worker?.postMessage({
        type: 'DOWNLOAD_URL_AND_DECODE_PACK',
        payload: {
          sessionId,
          athleteId,
          baseUrl,
          additionalParams
        }
      });
    });
  }

  terminate(): void {
    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
    }
  }
}
